import React from "react";
import moment from "moment";
import { getLocalDate, percentage } from "../../../../helpers";
import { MTableToolbar } from "@material-table/core";
import {
  moduleIdMap,
  soilKpis,
  substrateKpis,
} from "../../../../constants/kpis";

export function getMetricsValue(
  allData,
  group,
  metric,
  params,
  auto_drain_facility,
  conductivity_line,
  ec_division_factor,
  chart_runoff_multiplication_factor
) {
  var data = allData[group];

  try {
    let value = null;
    if (metric.substring(0, 3) === "ag_") {
      if (
        auto_drain_facility === "1" &&
        (metric === "ag_ph_in" ||
          metric === "ag_ph_out" ||
          metric === "ag_ec_in" ||
          metric === "ag_ec_out" ||
          metric === "ag_ec_sum" ||
          metric === "ag_ec_bag" ||
          metric === "ag_moisture" ||
          metric === "ag_water_cubes" ||
          metric === "ag_run_off")
      ) {
        const soil_climate_data = allData["soil_climate"];
        value = soil_climate_data[metric.slice(3)];
        if (!value && data[metric.slice(3)]) {
          value = data[metric.slice(3)];
        }
        if (metric.includes("ec") && value > 10) {
          var factor = ec_division_factor
            ? parseFloat(ec_division_factor)
            : 10000;
          return value / factor;
        }
      } else value = data[metric.slice(3)];
    } else {
      const irriTrigger = params && params[8] ? params[8].value : null;
      switch (metric) {
        case "mildew":
          value = Math.round(data.value);
          break;
        case "ambient_temp":
          value = data.temperature;
          break;
        case "humidity":
          value = percentage(Number(data.humidity), 1);
          break;
        case "irri_trigger":
          value = irriTrigger;
          break;
        case "predicted_irri":
          const moisture = data?.moisture_content;
          const waterUsage =
            typeof allData["past24hr_soilClimates_data"]["total_24hr_gains"] ===
            "string"
              ? 0 -
                parseFloat(
                  allData["past24hr_soilClimates_data"]["total_24hr_gains"]
                )
              : null;
          if (irriTrigger && moisture && waterUsage > 0) {
            const days = Math.floor(
              (moisture - irriTrigger) / Math.abs(waterUsage)
            );
            value = moment().add(days, "days").format("DD/MM/YY");
          }
          break;
        case "vic":
          // check if certain conductivity line is shown
          if (
            !conductivity_line ||
            conductivity_line === "default" ||
            conductivity_line === null
          ) {
            value = data[metric];
          } else {
            value = (data["vic_" + conductivity_line] / 1000).toFixed(2);
          }

          break;
        case "run_off":
          value =
            typeof allData["latest_run_off"]["run_off"] === "string"
              ? parseFloat(allData["latest_run_off"]["run_off"]).toFixed(2)
              : null;

          break;
        case "avg_24hr_run_off":
          value =
            typeof allData["past24hr_soilClimates_data"]["avg_24hr_run_off"] ===
            "string"
              ? parseFloat(
                  allData["past24hr_soilClimates_data"]["avg_24hr_run_off"]
                ).toFixed(2)
              : null;
          break;
        case "total_24hr_gains":
          value =
            typeof allData["past24hr_soilClimates_data"]["total_24hr_gains"] ===
            "string"
              ? (
                  0 -
                  parseFloat(
                    allData["past24hr_soilClimates_data"]["total_24hr_gains"]
                  )
                ).toFixed(2)
              : null;
          break;
        case "total_24hr_ml_in":
          value =
            typeof allData["past24hr_soilClimates_data"]["total_24hr_ml_in"] ===
            "string"
              ? (
                  parseFloat(
                    allData["past24hr_soilClimates_data"]["total_24hr_ml_in"]
                  ) * (chart_runoff_multiplication_factor ?? 1)
                ).toFixed(0)
              : null;
          break;
        case "total_24hr_ml_out":
          value =
            typeof allData["past24hr_soilClimates_data"][
              "total_24hr_ml_out"
            ] === "string"
              ? (
                  parseFloat(
                    allData["past24hr_soilClimates_data"]["total_24hr_ml_out"]
                  ) * (chart_runoff_multiplication_factor ?? 1)
                ).toFixed(0)
              : null;
          break;
        default:
          value = data[metric];
          break;
      }
    }
    return value;
  } catch (error) {
    return null;
  }
}

export function getMomentTimestamp(
  data,
  group,
  metric,
  timezone,
  auto_drain_facility
) {
  if (
    auto_drain_facility === "1" &&
    (metric === "ag_ph_in" ||
      metric === "ag_ph_out" ||
      metric === "ag_ec_in" ||
      metric === "ag_ec_out" ||
      metric === "ag_ec_sum" ||
      metric === "ag_moisture" ||
      metric === "ag_water_cubes" ||
      metric === "ag_run_off") &&
    data["soil_climate"]?.timestamp
  ) {
    return moment.utc(
      getLocalDate(data["soil_climate"].timestamp, timezone).format(
        "YYYY-MM-DD HH:mm:ss"
      )
    );
  }
  if (metric.includes("24hr")) {
    const date = data["past24hr_soilClimates_data"]?.local_date;
    return date ? moment.utc(date) : null;
  }
  if (!data[group]?.timestamp) {
    return null;
  }
  return moment.utc(
    getLocalDate(data[group].timestamp, timezone).format("YYYY-MM-DD HH:mm:ss")
  );
}

function getFilteredMetrics(
  data,
  kpis,
  metrics,
  group,
  timezone,
  params,
  auto_drain_facility,
  conductivity_line,
  ec_division_factor,
  chart_runoff_multiplication_factor
) {
  return metrics?.length
    ? metrics
        .filter(
          ({ ref, module_id }) =>
            module_id === moduleIdMap.get(group) && kpis[group]?.includes(ref)
        )
        .map(({ short_name, ref, suffix, thresholds }) => ({
          short_name,
          ref,
          suffix,
          thresholds,
          value: getMetricsValue(
            data,
            group,
            ref,
            params,
            auto_drain_facility,
            conductivity_line,
            ec_division_factor,
            chart_runoff_multiplication_factor
          ),
          parameters: ref === "moisture_content" ? data.parameters : null,
          timestamp: getMomentTimestamp(
            data,
            group,
            ref,
            timezone,
            auto_drain_facility
          ),
        }))
    : [];
}

export function getKpis(
  data,
  permissions,
  metrics,
  timezone,
  field_type,
  params,
  auto_drain_facility,
  conductivity_line,
  ec_division_factor,
  chart_runoff_multiplication_factor
) {
  if (!data) return [];
  const kpis = field_type === "soil" ? soilKpis : substrateKpis;

  const latest = [...moduleIdMap.keys()].reduce((arr, group) => {
    if (!permissions.includes(moduleIdMap.get(group))) return arr;
    return [
      ...arr,
      ...getFilteredMetrics(
        data,
        kpis,
        metrics,
        group,
        timezone,
        params,
        auto_drain_facility,
        conductivity_line,
        ec_division_factor,
        chart_runoff_multiplication_factor
      ),
    ];
  }, []);

  const daily = kpis?.daily
    ? [...moduleIdMap.keys()].reduce((arr, group) => {
        if (!permissions.includes(moduleIdMap.get(group))) return arr;
        return [
          ...arr,
          ...getFilteredMetrics(
            data,
            kpis.daily,
            metrics,
            group,
            timezone,
            params
          ),
        ];
      }, [])
    : [];
  return [...latest, ...daily];
}

export function getVisibility(field, visibility) {
  const settings = visibility.filter(({ field_id }) => field.id === field_id);
  if (!settings.length) {
    return { order: 999, hidden: false };
  }
  const { order, hidden } = settings[0];
  return { order, hidden };
}

export function fieldsWithKpis(fields, permissions, metrics) {
  return fields?.length
    ? fields.map(
        ({
          id,
          name,
          farm,
          field_type,
          crop_type,
          valve_group,
          irrimax,
          latest,
          auto_drain_facility,
          conductivity_line,
          timezone,
          ec_division_factor,
          chart_runoff_multiplication_factor,
        }) => ({
          id,
          name,
          farm,
          field_type,
          crop_type,
          valve_group,
          irrimax,
          data: getKpis(
            latest,
            permissions,
            metrics,
            timezone,
            field_type,
            latest?.parameters,
            auto_drain_facility,
            conductivity_line,
            ec_division_factor,
            chart_runoff_multiplication_factor
          ),
          parameters: latest?.parameters,
        })
      )
    : [];
}

export function filteredSortedFields(fields, { farm, crop_type, valve_group }) {
  return fields
    .filter(
      (field) =>
        !field.hidden &&
        (!farm || farm === field.farm) &&
        (!crop_type || crop_type === field.crop_type) &&
        (!valve_group || valve_group === field.valve_group)
    )
    .sort((a, b) => (a.order > b.order ? 1 : -1));
}

export function fieldsByType(fields, type) {
  return fields.filter(({ field_type }) => field_type === type);
}

export const defaultTableOptions = {
  search: false,
  padding: "dense",
  actionsColumnIndex: -1,
  exportButton: true,
};

export const analyticsTableToolbar = (theme) => ({
  Toolbar: (props) => (
    <div
      style={{
        backgroundColor: theme.palette.tertiary.light,
        color: theme.palette.tertiary.dark,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
      }}
    >
      <MTableToolbar {...props} />
    </div>
  ),
});
